import {
  CertificationExecutionStatusDto,
  CertificationStageTypeDto,
  StageExecutionStatusDto,
  TestTermParticipantStatusDto,
} from '@/api/generated';

export type CertificationTranslation = {
  card: {
    certificationStatus: Record<CertificationExecutionStatusDto, string>;
    manage: string;
    steps: string;
  };
  empty: {
    description: string;
    title: string;
  };
  header: {
    completed: string;
    inProgress: string;
    notStarted: string;
    sectionTitle: string;
    total: string;
  };
  step: {
    actions: {
      continue: string;
      openPlan: string;
      start: string;
      viewResults: string;
    };
    completedAt: string;
    completedCount: string;
    deadline: string;
    developmentCenter: {
      availableFrom: string;
      download: string;
      downloadFailed: string;
      status: {
        AWAITING_RELEASE: string;
        NO_OWN_REPORT: string;
        REPORTS_NOT_UPLOADED: string;
      };
    };
    examAttempt: {
      areasFailed: string;
      areasPassed: string;
      attemptOnDate: string;
      noAttempts: string;
      openExam: string;
      result: string;
      status: {
        CANCELLED: string;
        DRAFT: string;
        FAILED: string;
        PASSED: string;
        STARTED: string;
      };
      viewResult: string;
    };
    label: string;
    notActive: string;
    notConfigured: string;
    notInCertification: string;
    notPassed: string;
    progress: string;
    startedAt: string;
    status: Record<StageExecutionStatusDto, string>;
    surveyClosed: string;
    termExpiredInfo: string;
    termStatus: Record<TestTermParticipantStatusDto, string>;
    testWindowOpens: string;
    type: Record<CertificationStageTypeDto, string>;
  };
  title: string;
};

export const certificationTranslation: CertificationTranslation = {
  card: {
    certificationStatus: {
      [CertificationExecutionStatusDto.Completed]: 'Completed',
      [CertificationExecutionStatusDto.InProgress]: 'In Progress',
      [CertificationExecutionStatusDto.NotStarted]: 'Not Started',
    },
    manage: 'Manage',
    steps: '{{completed}}/{{total}} steps',
  },
  empty: {
    description: 'You have no certifications assigned yet.',
    title: 'No certifications assigned',
  },
  header: {
    completed: 'Completed',
    inProgress: 'In Progress',
    notStarted: 'Not Started',
    sectionTitle: 'Certification statistics',
    total: 'Total',
  },
  step: {
    actions: {
      continue: 'Continue',
      openPlan: 'Go to my plan',
      start: 'Start',
      viewResults: 'View Results',
    },
    completedAt: 'Completed {{date}}',
    completedCount: '{{count}}/{{total}} completed',
    deadline: 'Deadline {{date}}',
    developmentCenter: {
      availableFrom: 'Your report will be available from {{date}}.',
      download: 'Download report',
      downloadFailed: 'The report could not be downloaded. Please try again in a moment.',
      status: {
        AWAITING_RELEASE: 'A report will be available to download shortly.',
        NO_OWN_REPORT: 'No report — you did not take part in this stage.',
        REPORTS_NOT_UPLOADED: 'Reports have not been shared with participants yet.',
      },
    },
    examAttempt: {
      areasFailed: 'Failed:',
      areasPassed: 'Passed ({{passed}}/{{total}}):',
      attemptOnDate: 'Attempt on {{date}}',
      noAttempts: 'No exam attempts yet.',
      openExam: 'Open exam',
      result: 'Result {{percent}}%',
      status: {
        CANCELLED: 'Cancelled',
        DRAFT: 'Scheduled',
        FAILED: 'Failed',
        PASSED: 'Passed',
        STARTED: 'In progress',
      },
      viewResult: 'View result',
    },
    label: 'Step {{order}}',
    notActive: 'Self-assessment is not active yet',
    notConfigured: "This step hasn't been configured yet",
    notInCertification: 'This certification does not include this step',
    notPassed: 'Step not passed',
    progress: 'Progress',
    startedAt: 'Started {{date}}',
    status: {
      [StageExecutionStatusDto.Completed]: 'Completed',
      [StageExecutionStatusDto.Expired]: 'Expired',
      [StageExecutionStatusDto.InProgress]: 'In Progress',
      [StageExecutionStatusDto.NotAvailable]: 'Blocked',
      [StageExecutionStatusDto.NotInitialized]: 'Not Set Up',
      [StageExecutionStatusDto.NotStarted]: 'Not Started',
    },
    surveyClosed: 'Self-assessment is closed',
    termExpiredInfo: 'Time is up — this attempt has expired.',
    termStatus: {
      [TestTermParticipantStatusDto.Assigned]: 'Assigned',
      [TestTermParticipantStatusDto.Expired]: 'Expired',
      [TestTermParticipantStatusDto.Failed]: 'Failed',
      [TestTermParticipantStatusDto.Passed]: 'Passed',
      [TestTermParticipantStatusDto.Started]: 'Started',
    },
    testWindowOpens: 'From {{date}} the test will be active.',
    type: {
      [CertificationStageTypeDto.DevelopmentCenter]: 'Development Center',
      [CertificationStageTypeDto.Exam]: 'Practical test',
      [CertificationStageTypeDto.Plan]: 'IDP',
      [CertificationStageTypeDto.Survey]: 'Self-assessment',
      [CertificationStageTypeDto.Test]: 'Theory test',
    },
  },
  title: 'My Certifications',
};
